import type {
  AssignWorkerRequest,
  BomMaterialSwapRequest,
  BomMaterialSwapResponse,
  BomMaterialUsesQuery,
  BomMaterialUsesResponse,
  CreateProjectRequest,
  DuplicateProjectRequest,
  ListProjectsQuery,
  MergeProjectsRequest,
  ProjectMaterialUsage,
  ListProjectsResponse,
  ProjectAssignmentDto,
  ProjectBomReport,
  ProjectDetails,
  ProjectTimeReport,
  TransitionProjectStatusRequest,
  UpdateBomCoefficientRequest,
  UpdateBomCoefficientResponse,
  UpdateProjectRequest,
} from '@kreart/shared';
import { api, API_BASE_URL, ApiError } from '@/lib/api';
import { authStore } from '@/features/auth/store';

export function serialiseListQuery(query: ListProjectsQuery): string {
  const p = new URLSearchParams();
  if (query.view !== 'ACTIVE') p.set('view', query.view);
  if (query.page !== 1) p.set('page', String(query.page));
  if (query.pageSize !== 25) p.set('pageSize', String(query.pageSize));
  if (query.search) p.set('search', query.search);
  if (query.status) for (const s of query.status) p.append('status', s);
  if (query.clientId) p.set('clientId', query.clientId);
  if (query.agentId) for (const id of query.agentId) p.append('agentId', id);
  if (query.year) for (const year of query.year) p.append('year', String(year));
  if (query.month) for (const month of query.month) p.append('month', String(month));
  if (query.upcomingDeadline) p.set('upcomingDeadline', 'true');
  if (query.assignedToMe) p.set('assignedToMe', 'true');
  if (query.includeDeleted) p.set('includeDeleted', 'true');
  const qs = p.toString();
  return qs ? `?${qs}` : '';
}

export const projectsApi = {
  list: (query: ListProjectsQuery) =>
    api.get<ListProjectsResponse>(`/projects${serialiseListQuery(query)}`),
  get: (id: string) => api.get<ProjectDetails>(`/projects/${id}`),
  create: (body: CreateProjectRequest) => api.post<ProjectDetails>('/projects', body),
  /**
   * Independent copy of an existing project. The body carries the header fields
   * (possibly edited in the dialog); the rooms, line items and tender settings
   * come along server-side, and the copy always starts IN_PROGRESS.
   */
  duplicate: (sourceId: string, body: DuplicateProjectRequest) =>
    api.post<ProjectDetails>(`/projects/${sourceId}/duplicate`, body),
  /**
   * "Comprimă proiecte": one new project holding the rooms of all of these, which
   * go to "Proiecte șterse" as it is created.
   */
  merge: (body: MergeProjectsRequest) => api.post<ProjectDetails>('/projects/merge', body),
  /** The clone dialog's material section: every component reduced to its boards. */
  materialUsage: (id: string) => api.get<ProjectMaterialUsage>(`/projects/${id}/material-usage`),
  update: (id: string, body: UpdateProjectRequest) =>
    api.patch<ProjectDetails>(`/projects/${id}`, body),
  remove: (id: string) => api.delete<void>(`/projects/${id}`),
  archive: (id: string) => api.post<ProjectDetails>(`/projects/${id}/archive`, {}),
  unarchive: (id: string) => api.post<ProjectDetails>(`/projects/${id}/unarchive`, {}),
  restore: (id: string) => api.post<ProjectDetails>(`/projects/${id}/restore`, {}),
  /** Irreversible: removes the project and everything cascading off it. */
  purge: (id: string) => api.delete<void>(`/projects/${id}/permanent`),
  transition: (id: string, body: TransitionProjectStatusRequest) =>
    api.post<ProjectDetails>(`/projects/${id}/transition`, body),

  listAssignments: (id: string) => api.get<ProjectAssignmentDto[]>(`/projects/${id}/assignments`),
  assign: (id: string, body: AssignWorkerRequest) =>
    api.post<ProjectAssignmentDto>(`/projects/${id}/assignments`, body),
  unassign: (id: string, assignmentId: string) =>
    api.delete<void>(`/projects/${id}/assignments/${assignmentId}`),

  getBom: (id: string, roomId?: string) =>
    api.get<ProjectBomReport>(`/projects/${id}/bom${roomId ? `?roomId=${roomId}` : ''}`),
  /**
   * Set the markup on a BOM row: written onto every use behind it, with the
   * project re-priced server-side. `roomId` narrows it to the room whose BOM is
   * on screen.
   */
  setBomCoefficient: (id: string, body: UpdateBomCoefficientRequest) =>
    api.patch<UpdateBomCoefficientResponse>(`/projects/${id}/bom/coefficient`, body),
  /** Every use of one board behind a BOM row; with `toMaterialId`, the edge preview too. */
  bomMaterialUses: (id: string, query: BomMaterialUsesQuery) => {
    const params = new URLSearchParams({ materialId: query.materialId });
    if (query.roomId) params.set('roomId', query.roomId);
    if (query.toMaterialId) params.set('toMaterialId', query.toMaterialId);
    return api.get<BomMaterialUsesResponse>(`/projects/${id}/bom/material-uses?${params}`);
  },
  swapBomMaterial: (id: string, body: BomMaterialSwapRequest) =>
    api.post<BomMaterialSwapResponse>(`/projects/${id}/bom/material-swap`, body),
  getTime: (id: string) => api.get<ProjectTimeReport>(`/projects/${id}/time`),

  /**
   * Download a project's quote PDF as a Blob.
   *
   * Uses raw fetch (not apiFetch) because the response is binary and we need
   * the original filename from Content-Disposition. We still attach the
   * current access token from authStore the same way apiFetch does.
   */
  downloadQuotePdf: async (
    id: string,
    options: { detail?: 'full' } = {},
  ): Promise<{ blob: Blob; filename: string }> => {
    const token = authStore.snapshot.accessToken;
    const qs = options.detail ? `?detail=${options.detail}` : '';
    const res = await fetch(`${API_BASE_URL}/projects/${id}/quote.pdf${qs}`, {
      method: 'GET',
      credentials: 'include',
      headers: {
        accept: 'application/pdf',
        ...(token ? { authorization: `Bearer ${token}` } : {}),
      },
    });
    if (!res.ok) {
      const ct = res.headers.get('content-type') ?? '';
      const body: { code?: string; message?: string; requestId?: string } = ct.includes(
        'application/json',
      )
        ? await res.json().catch(() => ({}))
        : {};
      throw new ApiError(res.status, {
        code: body.code,
        message: body.message ?? `HTTP ${res.status}`,
        requestId: body.requestId,
      });
    }
    const disposition = res.headers.get('content-disposition') ?? '';
    const match = /filename="?([^";]+)"?/i.exec(disposition);
    const filename = match?.[1] ?? `oferta-${id}.pdf`;
    return { blob: await res.blob(), filename };
  },
};
